import { gql } from 'apollo-boost';
import { client } from '@/lib/api';
import { GetStripePricesQuery, GetStripePricesQueryVariables } from '@/generated/graphql';

export type InstitutionSubscription = {
  id: string;
  institutionUuid: string;
  start: string;
  end: string;
  status?: string;
};

export default {
  namespaced: true,

  state: () => ({
    plans: [] as any[],
    subscription: null as InstitutionSubscription | null,
    loading: false,
    error: null as string | null,
    sessionUrl: null as string | null,
  }),
  getters: {
    status: (state) => state.subscription?.status ?? null,
    isTrial: (state) => state.subscription?.status === 'TRIAL_ACTIVE',
    isCanceledByUser: (state) => state.subscription?.cancellationReason === 'cancellation_requested',
    endDate: (state) => {
      const end = state.subscription?.end;
      return end ? new Date(end * 1) : null;
    },
    isAdmin: (state) => {
      return state.subscription?.admin;
    },
    hadSub: (state) => {
      return state.subscription?.stripePlan?.priceId;
    },
  },
  mutations: {
    SET_LOADING(state, val: boolean) {
      state.loading = val;
    },
    SET_ERROR(state, val: string | null) {
      state.error = val;
    },
    SET_PLANS(state, plans: any[]) {
      state.plans = plans;
    },
    SET_SESSION_URL(state, url: string | null) {
      state.sessionUrl = url;
    },
    SET_SUBSCRIPTION(state, sub: InstitutionSubscription | null) {
      state.subscription = sub;
    },
  },

  actions: {
    async fetchPlans({ commit }) {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      try {
        const { data } = await client.query<GetStripePricesQuery, GetStripePricesQueryVariables>({
          query: gql`
            query GetStripePrices {
              stripePrices {
                id
                lookup_key
                nickname
                currency
                unit_amount
                tiers_mode
                tiers {
                  up_to
                  unit_amount
                  flat_amount
                }
                recurring {
                  interval
                  interval_count
                }
                product {
                  id
                  name
                  description
                  active
                }
              }
            }
          `,
          fetchPolicy: 'network-only',
        });
        commit('SET_PLANS', data.stripePrices || []);
      } catch (e: any) {
        commit('SET_ERROR', e.message);
      } finally {
        commit('SET_LOADING', false);
      }
    },
    async createBillingPortalSession({ commit, rootState }) {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      try {
        const { data } = await client.mutate({
          mutation: gql`
            mutation CreateBillingPortalSession($input: CreateBillingPortalInput!) {
              createBillingPortalSession(input: $input) {
                url
              }
            }
          `,
          variables: {
            input: {
              customerId: rootState.subscription.subscription?.stripeCustomerUuid,
              returnUrl: window.location.origin + '/billing',
            },
          },
        });
        const url = data?.createBillingPortalSession?.url;
        if (url) window.open(url, '_blank');
      } catch (e: any) {
        commit('SET_ERROR', e.message);
      } finally {
        commit('SET_LOADING', false);
      }
    },
    async getInstitutionSubscription({ commit }) {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      try {
        const { data } = await client.query({
          query: gql`
            query GetInstitutionSubscription {
              getInstitutionSubscription {
                id
                institutionUuid
                start
                end
                status
                stripeCustomerUuid
                stripePlan {
                  priceId
                  nickname
                  productName
                  amount
                  currency
                  interval
                }
                cancellationReason
                admin
              }
            }
          `,
          fetchPolicy: 'network-only',
        });
        commit('SET_SUBSCRIPTION', data?.getInstitutionSubscription ?? null);
      } catch (e: any) {
        commit('SET_ERROR', e.message);
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async createCheckoutSession({ commit, rootState }, { productId, priceId }) {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      try {
        const { data } = await client.mutate({
          mutation: gql`
            mutation CreateCheckoutSession($input: CreateCheckoutInput!) {
              createCheckoutSession(input: $input) {
                url
              }
            }
          `,
          variables: {
            input: {
              customerId: rootState.subscription.subscription?.stripeCustomerUuid,
              productId,
              priceId,
            },
          },
        });
        const url = data?.createCheckoutSession?.url;
        commit('SET_SESSION_URL', url);
        if (url) window.location.href = url;
      } catch (e: any) {
        commit('SET_ERROR', e.message);
      } finally {
        commit('SET_LOADING', false);
      }
    },
  },
};
